import "./App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { CustomerAuthProvider } from "./context/CustomerAuthContext";
import { CompanyProvider } from "./context/CompanyContext";

// iter165 — Kritik yol: yalnızca ilk ekranda kesin görünen bileşenler eager import edilir.
// Diğer tüm sayfalar route-based code-splitting ile lazy import — Trendyol seviyesinde ilk yükleme.
import PublicLayout from "./components/public/PublicLayout";
import ScrollToTop from "./components/ScrollToTop";
import Home from "./pages/public/Home";
import Catalog from "./pages/public/Catalog";
import ProductDetail from "./pages/public/ProductDetail";
import Cart from "./pages/public/Cart";
import CustomerAuth from "./pages/public/CustomerAuth";

// Lazy public (nadir/deep)
const Checkout = lazy(() => import("./pages/public/Checkout"));
const MyOrders = lazy(() => import("./pages/public/MyOrders"));
const MyPortal = lazy(() => import("./pages/public/MyPortal"));
const CustomerSetPassword = lazy(() => import("./pages/public/CustomerSetPassword"));
const CorporatePage = lazy(() => import("./pages/public/CorporatePage"));
const StoreLocator = lazy(() => import("./pages/public/StoreLocator"));
const StoreDetail = lazy(() => import("./pages/public/StoreDetail"));
const LocalLandingPage = lazy(() => import("./pages/public/LocalLandingPage"));
const ComparePage = lazy(() => import("./pages/public/ComparePage"));
const Blog = lazy(() => import("./pages/public/Blog"));
const BlogPost = lazy(() => import("./pages/public/BlogPost"));
const CategorySEOPage = lazy(() => import("./pages/public/CategorySEOPage"));
const Campaigns = lazy(() => import("./pages/public/Campaigns"));
const ProductTree = lazy(() => import("./pages/public/ProductTree"));
const AuthCallback = lazy(() => import("./pages/public/AuthCallback"));
const HelpSupport = lazy(() => import("./pages/public/HelpSupport"));

// Lazy Admin (ERP) — hepsi
const AdminLogin = lazy(() => import("./pages/Login"));
const Layout = lazy(() => import("./components/Layout"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Purchases = lazy(() => import("./pages/Purchases"));
const Customers = lazy(() => import("./pages/Customers"));
const CustomerDetail = lazy(() => import("./pages/CustomerDetail"));
const Suppliers = lazy(() => import("./pages/Suppliers"));
const SupplierDetail = lazy(() => import("./pages/SupplierDetail"));
const Quotes = lazy(() => import("./pages/Quotes"));
const Orders = lazy(() => import("./pages/Orders"));
const Stock = lazy(() => import("./pages/Stock"));
const Reports = lazy(() => import("./pages/Reports"));
const ReportsHub = lazy(() => import("./pages/ReportsHub"));
const ReportSalesBreakdown = lazy(() => import("./pages/reports/ReportSalesBreakdown"));
const ReportCustomerAging = lazy(() => import("./pages/reports/ReportCustomerAging"));
const ReportPendingShipments = lazy(() => import("./pages/reports/ReportPendingShipments"));
const ReportStockEfficiency = lazy(() => import("./pages/reports/ReportStockEfficiency"));
const ReportProfitLoss = lazy(() => import("./pages/reports/ReportProfitLoss"));
const ReportTopProducts = lazy(() => import("./pages/reports/ReportTopProducts"));
const ReportSupplierPerformance = lazy(() => import("./pages/reports/ReportSupplierPerformance"));
const ReportTopCustomers = lazy(() => import("./pages/reports/ReportTopCustomers"));
const ReportCashFlowForecast = lazy(() => import("./pages/reports/ReportCashFlowForecast"));
const ReportVatSummary = lazy(() => import("./pages/reports/ReportVatSummary"));
const ReportPurchaseBreakdown = lazy(() => import("./pages/reports/ReportPurchaseBreakdown"));
const ReportStockValue = lazy(() => import("./pages/reports/ReportStockValue"));
const ReportBalanceSheet = lazy(() => import("./pages/reports/ReportBalanceSheet"));
const ReportCartAbandonment = lazy(() => import("./pages/reports/ReportCartAbandonment"));
const ReportConversionFunnel = lazy(() => import("./pages/reports/ReportConversionFunnel"));
const Users = lazy(() => import("./pages/Users"));
const SalesTargets = lazy(() => import("./pages/SalesTargets"));
const AuditLog = lazy(() => import("./pages/AuditLog"));
const Trash = lazy(() => import("./pages/Trash"));
const TwoFactorSetup = lazy(() => import("./pages/TwoFactorSetup"));
const WhatsAppTemplates = lazy(() => import("./pages/WhatsAppTemplates"));
const ProfileSettings = lazy(() => import("./pages/ProfileSettings"));
const Team = lazy(() => import("./pages/Team"));
const HR = lazy(() => import("./pages/HR"));
const UserDetail = lazy(() => import("./pages/UserDetail"));
const Categories = lazy(() => import("./pages/Categories"));
const Tasks = lazy(() => import("./pages/Tasks"));
const Projects = lazy(() => import("./pages/Projects"));
const QuoteFollowupSettings = lazy(() => import("./pages/QuoteFollowupSettings"));
const SettingsCompany = lazy(() => import("./pages/SettingsCompany"));
const Settings = lazy(() => import("./pages/Settings"));
const HomepageCms = lazy(() => import("./pages/HomepageCms"));
const BrandScraper = lazy(() => import("./pages/BrandScraper"));
const SettingsNotes = lazy(() => import("./pages/SettingsNotes"));
const SettingsSystem = lazy(() => import("./pages/SettingsSystem"));
const SettingsStock = lazy(() => import("./pages/SettingsStock"));
const SettingsRoles = lazy(() => import("./pages/SettingsRoles"));
const SettingsUnits = lazy(() => import("./pages/SettingsUnits"));
const UretimDisi = lazy(() => import("./pages/UretimDisi"));
const StockAudit = lazy(() => import("./pages/StockAudit"));
const Locations = lazy(() => import("./pages/Locations"));
const CashAccounts = lazy(() => import("./pages/CashAccounts"));
const BankAccounts = lazy(() => import("./pages/BankAccounts"));
const CreditCards = lazy(() => import("./pages/CreditCards"));
const Invoices = lazy(() => import("./pages/Invoices"));
const EBelgeler = lazy(() => import("./pages/EBelgeler"));
const Brands = lazy(() => import("./pages/Brands"));
const BulkPricing = lazy(() => import("./pages/BulkPricing"));
const Collections = lazy(() => import("./pages/Collections"));
const Attributes = lazy(() => import("./pages/Attributes"));
const Products = lazy(() => import("./pages/Products"));
const ProductDetailAdmin = lazy(() => import("./pages/ProductDetail"));
const Fleet = lazy(() => import("./pages/Fleet"));
const Shipments = lazy(() => import("./pages/Shipments"));
const ChannelView = lazy(() => import("./pages/ChannelView"));
const PurchaseOrders = lazy(() => import("./pages/PurchaseOrders"));
const Purchasing = lazy(() => import("./pages/Purchasing"));
const PublicMutabakat = lazy(() => import("./pages/PublicMutabakat"));
const Hepsiburada = lazy(() => import("./pages/Hepsiburada"));  // legacy stub — kept only for backward-compat imports; route redirects to /panel/pazaryeri
const PublicQuoteShare = lazy(() => import("./pages/PublicQuoteShare"));
const ImageHunter = lazy(() => import("./pages/ImageHunter"));
const ImageIntelligence = lazy(() => import("./pages/ImageIntelligence"));
const ProductMovements = lazy(() => import("./pages/ProductMovements"));
const ProductMarketing = lazy(() => import("./pages/ProductMarketing"));
const Cheques = lazy(() => import("./pages/Cheques"));
const PayrollDashboard = lazy(() => import("./pages/PayrollDashboard"));
const Notes = lazy(() => import("./pages/Notes"));
const Barter = lazy(() => import("./pages/Barter"));
const VadeTakvimi = lazy(() => import("./pages/VadeTakvimi"));
const ShippingRules = lazy(() => import("./pages/ShippingRules"));
const EcommerceAnalytics = lazy(() => import("./pages/EcommerceAnalytics"));
const MarketplaceDashboard = lazy(() => import("./pages/MarketplaceDashboard"));
const MarketplaceHub = lazy(() => import("./pages/MarketplaceHub"));
const ShippingSettings = lazy(() => import("./pages/ShippingSettings"));
const Expenses = lazy(() => import("./pages/Expenses"));
const ExpenseCategories = lazy(() => import("./pages/ExpenseCategories"));
const PurchaseDetail = lazy(() => import("./pages/PurchaseDetail"));
const FuelTracking = lazy(() => import("./pages/FuelTracking"));
const EmployeeDetail = lazy(() => import("./pages/EmployeeDetail"));
const CommissionRules = lazy(() => import("./pages/CommissionRules"));
const EmployeePortal = lazy(() => import("./pages/EmployeePortal"));
const CompanyCenter = lazy(() => import("./pages/CompanyCenter"));
const Archive = lazy(() => import("./pages/Archive"));
const Contracts = lazy(() => import("./pages/Contracts"));
const ArchiveCategory = lazy(() => import("./pages/ArchiveCategory"));
const ArchiveUpload = lazy(() => import("./pages/ArchiveUpload"));
const ArchiveCustomerTimeline = lazy(() => import("./pages/ArchiveCustomerTimeline"));
const ArchiveVerify = lazy(() => import("./pages/ArchiveVerify"));
const SettingsSEO = lazy(() => import("./pages/SettingsSEO"));
const CorporatePageEditor = lazy(() => import("./pages/CorporatePageEditor"));
const BlogAdmin = lazy(() => import("./pages/BlogAdmin"));
const AiSeoSettings = lazy(() => import("./pages/AiSeoSettings"));
const AiAssistantAdmin = lazy(() => import("./pages/AiAssistantAdmin"));
const RiskDashboard = lazy(() => import("./pages/RiskDashboard"));

const RouteFallback = () => (
  <div className="min-h-[40vh] flex items-center justify-center text-zinc-400 text-sm">
    <span className="inline-flex items-center gap-2">
      <span className="w-2 h-2 rounded-full bg-primary animate-pulse"/>
      Yükleniyor…
    </span>
  </div>
);

const AdminProtected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Yükleniyor…</div>;
  if (!user) return <Navigate to="/panel/login" replace />;
  if (user.role === "customer") return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Toaster position="top-right" richColors />
        <CompanyProvider>
        <CustomerAuthProvider>
          <CartProvider>
            <AuthProvider>
              <Suspense fallback={<RouteFallback/>}>
              <Routes>
                {/* Public storefront */}
                <Route path="/" element={<PublicLayout />}>
                  <Route index element={<Home />} />
                  <Route path="urunler" element={<Catalog />} />
                  <Route path="urun/:sku" element={<ProductDetail />} />
                  <Route path="sepet" element={<Cart />} />
                  <Route path="odeme" element={<Checkout />} />
                  <Route path="hesap/giris" element={<CustomerAuth />} />
                  <Route path="hesap/parola-belirle/:token" element={<CustomerSetPassword />} />
                  <Route path="hesap" element={<MyPortal />} />
                  <Route path="hesap/siparislerim" element={<MyOrders />} />
                  {/* iter126 — SEO Corporate Public Pages */}
                  <Route path="magazalar" element={<StoreLocator />} />
                  <Route path="magaza/:sid" element={<StoreDetail />} />
                  {/* iter127 — Blog */}
                  <Route path="blog" element={<Blog />} />
                  <Route path="blog/:slug" element={<BlogPost />} />
                  {/* iter128 — Kalıcı kategori URL'i */}
                  <Route path="kategori/:slug" element={<CategorySEOPage />} />
                  {/* iter134 — Kampanyalı Ürünler */}
                  <Route path="firsatlar" element={<Campaigns />} />
                  <Route path="kampanyalar" element={<Navigate to="/firsatlar" replace />} />
                  {/* iter135 — Ürün Ağacı (Faz 3) */}
                  <Route path="urun-agaci" element={<ProductTree />} />
                  <Route path="yardim" element={<HelpSupport />} />
                  {/* iter154 — GEO: Yerel Landing + Karşılaştırma */}
                  <Route path="bolge/:city" element={<LocalLandingPage />} />
                  <Route path="bolge/:city/:district" element={<LocalLandingPage />} />
                  <Route path="bolge/:city/:district/:hood" element={<LocalLandingPage />} />
                  <Route path="karsilastir" element={<ComparePage />} />
                  {/* iter135 — Google OAuth callback (Faz 4) */}
                  <Route path="auth/callback" element={<AuthCallback />} />
                  <Route path=":slug" element={<CorporatePage />} />
                </Route>

                {/* iter146 (Faz 3) — Public Mutabakat imza sayfası */}
                <Route path="/mutabakat/:token" element={<PublicMutabakat />} />

                {/* Admin / ERP */}
                <Route path="/panel/login" element={<AdminLogin />} />
                <Route path="/panel" element={<AdminProtected><Layout /></AdminProtected>}>
                  <Route index element={<Dashboard />} />
                  <Route path="satis" element={<Navigate to="/panel/siparisler" replace />} />
                  <Route path="alis" element={<Purchases />} />
                  <Route path="alis/:id" element={<PurchaseDetail />} />
                  <Route path="giderler" element={<Expenses />} />
                  <Route path="gider-kategori" element={<ExpenseCategories />} />
                  <Route path="yakit" element={<FuelTracking />} />
                  <Route path="musteriler" element={<Customers />} />
                  <Route path="musteriler/:id" element={<CustomerDetail />} />
                  <Route path="tedarikciler" element={<Suppliers />} />
                  <Route path="tedarikciler/:id" element={<SupplierDetail />} />
                  <Route path="teklifler" element={<Quotes />} />
                  <Route path="siparisler" element={<Orders />} />
                  <Route path="stok" element={<Stock />} />
                  <Route path="kategoriler" element={<Categories />} />
                  <Route path="projeler" element={<Projects />} />
                  <Route path="ik" element={<Navigate to="/panel/kullanicilar" replace />} />
                  <Route path="ik/:id" element={<Navigate to="/panel/kullanicilar" replace />} />
                  <Route path="prim-kurallari" element={<CommissionRules />} />
                  <Route path="hesabim" element={<EmployeePortal />} />
                  <Route path="sirket" element={<CompanyCenter />} />
                  <Route path="raporlar" element={<ReportsHub />} />
                  <Route path="raporlar/eski" element={<Reports />} />
                  <Route path="raporlar/satis-kirilimi" element={<ReportSalesBreakdown />} />
                  <Route path="raporlar/musteri-yaslandirma" element={<ReportCustomerAging />} />
                  <Route path="raporlar/bekleyen-sevkiyat" element={<ReportPendingShipments />} />
                  <Route path="raporlar/stok-verimlilik" element={<ReportStockEfficiency />} />
                  <Route path="raporlar/kar-zarar" element={<ReportProfitLoss />} />
                  <Route path="raporlar/en-cok-satan" element={<ReportTopProducts />} />
                  <Route path="raporlar/tedarikci-performans" element={<ReportSupplierPerformance />} />
                  <Route path="raporlar/top-musteri" element={<ReportTopCustomers />} />
                  <Route path="raporlar/nakit-akis" element={<ReportCashFlowForecast />} />
                  <Route path="raporlar/kdv" element={<ReportVatSummary />} />
                  <Route path="raporlar/alis-analizi" element={<ReportPurchaseBreakdown />} />
                  <Route path="raporlar/stok-degeri" element={<ReportStockValue />} />
                  <Route path="raporlar/bilanco" element={<ReportBalanceSheet />} />
                  <Route path="raporlar/sepet-terk" element={<ReportCartAbandonment />} />
                  <Route path="raporlar/donusum" element={<ReportConversionFunnel />} />
                  <Route path="kullanicilar" element={<Users />} />
                  <Route path="kullanicilar/:uid" element={<UserDetail />} />
                  <Route path="bordro" element={<PayrollDashboard />} />
                  <Route path="satis-hedefleri" element={<SalesTargets />} />
                  <Route path="guvenlik/denetim" element={<AuditLog />} />
                  <Route path="guvenlik/cop-kutusu" element={<Trash />} />
                  <Route path="hesabim/2fa" element={<TwoFactorSetup />} />
                  <Route path="profilim" element={<ProfileSettings />} />
                  <Route path="ekip" element={<Team />} />
                  {/* iter154.4 — Ayarlar tek panel: Sheet ile açılır. Doğrudan /panel/ayarlar URL'ini panele yönlendir. */}
                  <Route path="ayarlar" element={<Navigate to="/panel" replace />} />
                  <Route path="ayarlar/whatsapp-sablonlari" element={<WhatsAppTemplates />} />
                  <Route path="ayarlar/kurumsal" element={<SettingsCompany />} />
                  <Route path="ayarlar/anasayfa-cms" element={<HomepageCms />} />
                  <Route path="marka-kaziyici" element={<BrandScraper />} />
                  <Route path="ayarlar/notlar" element={<SettingsNotes />} />
                  <Route path="ayarlar/sistem" element={<SettingsSystem />} />
                  <Route path="ayarlar/stok-kurallari" element={<SettingsStock />} />
                  <Route path="ayarlar/roller" element={<SettingsRoles />} />
                  <Route path="ayarlar/birimler" element={<SettingsUnits />} />
                  <Route path="stok-sayim" element={<StockAudit />} />
                  <Route path="gorevler" element={<Tasks />} />
                  <Route path="projeler" element={<Projects />} />
                  <Route path="teklif-takip" element={<QuoteFollowupSettings />} />
                  <Route path="kasa" element={<CashAccounts />} />
                  <Route path="banka" element={<BankAccounts />} />
                  <Route path="kredi-kartlari" element={<CreditCards />} />
                  <Route path="faturalar" element={<Invoices />} />
                  <Route path="e-belgeler" element={<EBelgeler />} />
                  <Route path="mekanlar" element={<Locations />} />
                  <Route path="ayarlar/depolar" element={<Locations />} />
                  <Route path="urunler" element={<Products />} />
                  <Route path="uretim-disi" element={<UretimDisi />} />
                  <Route path="urunler/:id" element={<ProductDetailAdmin />} />
                  <Route path="urunler/:id/hareketler" element={<ProductMovements />} />
                  <Route path="urunler/:id/pazarlama" element={<ProductMarketing />} />
                  <Route path="cek" element={<Cheques />} />
                  <Route path="senet" element={<Notes />} />
                  <Route path="barter" element={<Barter />} />
                  <Route path="risk" element={<RiskDashboard />} />
                  <Route path="vade-takvimi" element={<VadeTakvimi />} />
                  <Route path="nakliyat" element={<ShippingRules />} />
                  <Route path="e-ticaret-analitik" element={<EcommerceAnalytics />} />
                  <Route path="toplu-fiyat" element={<BulkPricing />} />
                  <Route path="markalar" element={<Brands />} />
                  <Route path="koleksiyonlar" element={<Collections />} />
                  <Route path="ozellikler" element={<Attributes />} />
                  <Route path="lojistik/sevkiyatlar" element={<Shipments />} />
                  <Route path="lojistik/araclar" element={<Fleet />} />
                  <Route path="kanal/eticaret" element={<ChannelView channel="eticaret" />} />
                  <Route path="kanal/perakende" element={<ChannelView channel="perakende" />} />
                  <Route path="kanal/proje-satis" element={<ChannelView channel="proje" />} />
                  <Route path="satin-alma-siparisleri" element={<PurchaseOrders />} />
                  <Route path="satin-alma-talepleri" element={<Purchasing />} />
                  <Route path="hepsiburada" element={<Navigate to="/panel/pazaryeri?tab=integrations" replace />} />
                  <Route path="pazaryeri" element={<MarketplaceHub />} />
                  <Route path="hepsiburada-legacy" element={<Navigate to="/panel/pazaryeri?tab=integrations" replace />} />
                  <Route path="sevkiyat-ayarlari" element={<ShippingSettings />} />
                  <Route path="marketplace-dashboard" element={<Navigate to="/panel/pazaryeri?tab=analytics" replace />} />
                  <Route path="gorsel-avcisi" element={<ImageHunter />} />
                  <Route path="gorsel-zeka" element={<ImageIntelligence />} />
                  <Route path="arsiv" element={<Archive />} />
                  <Route path="hukuk" element={<Contracts />} />
                  <Route path="arsiv/yukle" element={<ArchiveUpload />} />
                  <Route path="arsiv/musteri/:cid" element={<ArchiveCustomerTimeline />} />
                  <Route path="arsiv/:key" element={<ArchiveCategory />} />
                  <Route path="ayarlar/seo" element={<SettingsSEO />} />
                  <Route path="ayarlar/kurumsal-sayfalar/:slug" element={<CorporatePageEditor />} />
                  <Route path="blog" element={<BlogAdmin />} />
                  <Route path="ayarlar/ai-seo" element={<AiSeoSettings />} />
                  <Route path="ayarlar/ai-asistan" element={<AiAssistantAdmin />} />
                </Route>

                {/* legacy redirects */}
                <Route path="/login" element={<Navigate to="/panel/login" replace />} />
                {/* iter65 — Public paylaşılabilir teklif linki */}
                <Route path="/q/:token" element={<PublicQuoteShare />} />
                {/* iter146 — Backward compat: eski maillerde /paylas/teklif/:token */}
                <Route path="/paylas/teklif/:token" element={<PublicQuoteShare />} />
                {/* iter125 — Public QR doğrulama */}
                <Route path="/arsiv/dogrula/:sha" element={<ArchiveVerify />} />
              </Routes>
              </Suspense>
            </AuthProvider>
          </CartProvider>
        </CustomerAuthProvider>
        </CompanyProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
